import { useEffect, useState } from 'react'

// Steps through frames on a timer, looping, while `playing` is true.
//
// Pausing keeps the current frame on screen rather than resetting: a reader
// who pauses to read the answer should get to read it. One timeout at a time,
// cleared on every change, so toggling quickly or unmounting never leaves a
// second timer running.
export default function useScriptPlayer(frames, playing) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!playing || frames.length === 0) return undefined
    const id = setTimeout(
      () => setIndex((i) => (i + 1) % frames.length),
      frames[index].duration,
    )
    return () => clearTimeout(id)
  }, [frames, index, playing])

  return frames[index]
}

// Whether the reader asked their system for less motion, kept up to date if
// they change it with the page open.
export function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)'
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia?.(query)
    if (!mq) return undefined
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

// Whether `ref`'s element is on screen and the tab is visible. Nothing on the
// page should be animating for nobody: a loop scrolled out of view or left in
// a background tab is a timer firing for no one, and on a phone a battery
// cost too.
export function useIsWatched(ref) {
  const [inView, setInView] = useState(true)
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  )

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting))
    io.observe(el)
    return () => io.disconnect()
  }, [ref])

  useEffect(() => {
    const onChange = () => setPageVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  return inView && pageVisible
}
